import {
  createSlice,
  createEntityAdapter,
  PayloadAction,
} from "@reduxjs/toolkit";

export type TransactionSource = "plaid" | "user_created";

export interface Transaction {
  id: string;
  externalId?: string | undefined;
  plaidTransactionId?: string; // Optional
  userId: string;
  accountId: string;
  institutionId?: number; // Optional
  amount: number; // You might use 'string' if you prefer preserving precision from a Decimal
  date: string; // ISO date string
  name?: string | undefined;
  category?: string;
  source: TransactionSource;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  bankAccountId?: string;
  categoryConfidence?: number; // Optional confidence score for the category
  categorySource?: string; // Optional source of the category
  categorizedAt?: string; // Optional ISO date string when the transaction was categorized
  propertyId?: string; // Optional property ID for association with a property
  isReviewed: boolean; // Indicates if the transaction has been reviewed
  isIgnored: boolean; // Indicates if the transaction should be ignored in calculations
}

// Create an adapter for transactions.
export const transactionsAdapter = createEntityAdapter<Transaction>({
  sortComparer: (a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime(),
});

// Define the initial state using the adapter.
const initialState = transactionsAdapter.getInitialState();

// Create a slice for transactions.
const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    // Replaces the current transactions with a new list.
    transactionsReceived: (state, action: PayloadAction<Transaction[]>) => {
      transactionsAdapter.setAll(state, action.payload);
    },
    // Adds a single new transaction.
    transactionAdded: (state, action: PayloadAction<Transaction>) => {
      transactionsAdapter.addOne(state, action.payload);
    },
    // New update action for updating an existing transaction.
    transactionUpdated: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<Transaction> }>,
    ) => {
      transactionsAdapter.updateOne(state, action.payload);
    },
    transactionRemoved: (state, action: PayloadAction<string>) => {
      transactionsAdapter.removeOne(state, action.payload);
    },
  },
});

// Export the action creators.
export const {
  transactionsReceived,
  transactionAdded,
  transactionUpdated,
  transactionRemoved,
} = transactionsSlice.actions;

// Export the reducer for inclusion in the Redux store.
export default transactionsSlice.reducer;
